// NumericInput is derived from Ant Design's official documentation.

import { Input, Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import React, { useState } from 'react';

interface NumericInputProps {
  className?: string;
  disabled?: boolean;
  id?: string;
  maxLength?: number;
  onChange?: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  /** Show number preview with decimal separator */
  showTooltip?: boolean;
  /** By default we use formatted value as the dynamic tooltip title */
  staticTooltipTitle?: string;
  suffix?: React.ReactNode;
  tooltipPlacement?: TooltipPlacement;
  value?: string;
}

const formatNumber = (value: number) =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 20
  }).format(value);

/**
 * Use the Input in conjunction with Tooltip component to create a Numeric Input,
 * which provides a good experience for extra-long content display.
 */
const MFBNumericInput = ({
  maxLength = 16,
  onChange,
  placeholder,
  showTooltip = false,
  staticTooltipTitle,
  tooltipPlacement = 'topLeft',
  value,
  ...rest
}: NumericInputProps) => {

  const [enableTooltip, setEnableTooltip] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = e.target;
    const reg = /^-?\d*(\.\d*)?$/;
    if (reg.test(inputValue) || inputValue === '' || inputValue === '-') {
      onChange?.(inputValue);
    }
    setEnableTooltip(true);
  };

  // Not allow '.' at the end or only '-' in the input box
  // normalize() works not as expected
  const handleBlur = () => {
    let inputValue = value?.toString();
    if (
      inputValue?.charAt(inputValue.length - 1) === '.' ||
      inputValue === '-'
    ) {
      inputValue = inputValue.slice(0, -1);
    }
    onChange?.(inputValue?.replace(/0*(\d+)/, '$1') ?? '');
    setEnableTooltip(false);
  };

  const title = (() => {
    if (staticTooltipTitle) {
      return staticTooltipTitle;
    }
    return value ? (
      <span>{value !== '-' ? formatNumber(Number(value)) : '-'}</span>
    ) : (
      placeholder
    );
  })();

  return (
    <Tooltip
      visible={showTooltip && enableTooltip}
      trigger={['focus']}
      title={title}
      placement={tooltipPlacement}
    >
      <Input
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        {...rest}
      />
    </Tooltip>
  );
};

export default MFBNumericInput;
